import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Bell, ChevronDown, X, Plus } from 'lucide-react';
import { TODAY, df } from '../utils/dates';
import { computeGoalProgress, formatTargetDate, computeGoalStatus } from '../utils/goals';
import CyclingTitle from './primitives/CyclingTitle';
import PulseCell   from './primitives/PulseCell';
import EmptyState  from './primitives/EmptyState';
import css from './FinancePage.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const FREQ_OPTIONS = [
  { value: 'weekly',   label: 'weekly',    mo: 52/12 },
  { value: 'biweekly', label: 'bi-weekly', mo: 26/12 },
  { value: 'monthly',  label: 'monthly',   mo: 1     },
  { value: 'annual',   label: 'annual',    mo: 1/12  },
];

function freqLabel(f) {
  return f === 'weekly' ? 'wk' : f === 'biweekly' ? '2wk' : f === 'annual' ? 'yr' : 'mo';
}
function monthlyEquiv(amount, freq) {
  const f = FREQ_OPTIONS.find(o => o.value === freq);
  return amount * (f ? f.mo : 1);
}
function calcDeduction(inc, field) {
  const amount = parseFloat(inc[field] || 0);
  const type   = inc[`${field}Type`] || 'fixed';
  if (isNaN(amount) || amount === 0) return 0;
  if (type === 'pct') return (parseFloat(inc.amount || 0) * amount) / 100;
  return amount;
}
function fmtCurrency(n) {
  return '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function relDue(dateStr) {
  if (!dateStr) return null;
  const d = df(dateStr);
  if (d < 0)   return `${Math.abs(d)}d overdue`;
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d <= 6)  return `in ${d}d`;
  return null;
}

const DEFAULT_CATS = ['Housing', 'Food', 'Transport', 'Health', 'Entertainment', 'Subscriptions', 'Other'];
const GOAL_TYPES   = ['savings', 'debt-payoff', 'contribution'];
const FINANCE_FILTERS = ['unified', 'ledger', 'spend', 'earn', 'invest', 'goals'];

const EMPTY_GOAL_FORM = {
  title: '', type: 'savings', targetAmount: '', currentAmount: '',
  targetDate: '', linkedAccount: '', priority: 'normal',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SignalRow({ label, value, accent, sub }) {
  return (
    <div className={css.signalRow}>
      <div>
        <span className={css.signalLabel}>{label}</span>
        {sub && <div className={css.signalSub}>{sub}</div>}
      </div>
      <span className={[css.signalValue,
        accent === 'gold' ? css.signalGold :
        accent === 'soft' ? css.signalSoft :
        accent === 'deep' ? css.signalDeep : ''].filter(Boolean).join(' ')}>
        {value}
      </span>
    </div>
  );
}

function SectionHeader({ label, meta }) {
  return (
    <div className={css.sectionHdr}>
      <span className={css.sectionLabel}>{label}</span>
      {meta && <span className={css.sectionMeta}>{meta}</span>}
    </div>
  );
}

function SelectWrap({ children }) {
  return (
    <div className={css.selectWrap}>
      {children}
      <ChevronDown size={12} strokeWidth={1.5} className={css.selectChevron} />
    </div>
  );
}

function TypeToggle({ value, onChange }) {
  return (
    <div className={css.typeToggle}>
      {['fixed','pct'].map(t => (
        <button key={t} type="button"
          className={[css.typeSeg, value === t ? css.typeSegActive : ''].filter(Boolean).join(' ')}
          onClick={() => onChange(t)}>{t === 'fixed' ? '$' : '%'}</button>
      ))}
    </div>
  );
}

function InlineExpenseEdit({ editData, setEditData, allCats, onSave, onCancel, onDelete }) {
  return (
    <div className={css.inlineEdit}>
      <input className={css.editInput} value={editData.name || ''} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} placeholder="name" autoFocus />
      <div className={css.formGrid2}>
        <input className={css.editInput} type="number" value={editData.amount || ''} onChange={e => setEditData(p => ({ ...p, amount: e.target.value }))} />
        <SelectWrap>
          <select className={css.editSelect} value={editData.freq || 'monthly'} onChange={e => setEditData(p => ({ ...p, freq: e.target.value }))}>
            {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </SelectWrap>
      </div>
      <div className={css.formGrid2}>
        <input className={css.editInput} type="date" value={editData.next || ''} onChange={e => setEditData(p => ({ ...p, next: e.target.value }))} />
        <SelectWrap>
          <select className={css.editSelect} value={editData.category || ''} onChange={e => setEditData(p => ({ ...p, category: e.target.value }))}>
            {allCats.map(c => <option key={c} value={c}>{c.toLowerCase()}</option>)}
          </select>
        </SelectWrap>
      </div>
      <div className={css.editActions}>
        <button className={css.editSave} onClick={onSave}>save</button>
        <button className={css.editCancel} onClick={onCancel}>cancel</button>
        <button className={css.editDelete} onClick={onDelete}>delete</button>
      </div>
    </div>
  );
}

// Goal card — tappable, shows progress bar + numbers
function GoalCard({ goal, onEdit }) {
  const pct       = computeGoalProgress(goal);
  const status    = computeGoalStatus(goal);
  const targetStr = formatTargetDate(goal.targetDate);
  const isDebt    = goal.type === 'debt-payoff';
  const barColor  = isDebt ? 'var(--accent-g)' : 'var(--accent-s)';

  const progressLabel = isDebt
    ? `${fmtCurrency(goal.currentAmount)} remaining (of ${fmtCurrency(goal.targetAmount)})`
    : goal.type === 'contribution'
      ? `${fmtCurrency(goal.currentAmount)} contributed (of ${fmtCurrency(goal.targetAmount)})`
      : `${fmtCurrency(goal.currentAmount)} of ${fmtCurrency(goal.targetAmount)}`;

  const pctLabel = isDebt ? `${Math.round(pct)}% paid` : `${Math.round(pct)}%`;

  // Pace line
  const paceLine = (() => {
    if (status.status === 'complete') return 'done · hit target';
    if (status.status === 'no-target-date') return null;
    if (!status.projectedFinishDate) return null;
    const proj = status.projectedFinishDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toLowerCase();
    const tgt  = goal.targetDate ? new Date(goal.targetDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toLowerCase() : '';
    return `at this pace, done ${proj} · target ${tgt}`;
  })();

  const statusLabel = {
    'ahead':          '↗ ahead',
    'on-track':       'on track',
    'behind':         '↘ behind',
    'at-risk':        '⚠ at risk',
    'complete':       '✓ complete',
    'no-target-date': null,
  }[status.status];

  return (
    <div className={css.goalCard} onClick={onEdit}>
      {/* Row 1: header */}
      <div className={css.goalHdr}>
        <div className={css.goalHdrLeft}>
          <span className={css.goalTitle}>{goal.title}</span>
          <span className={css.goalType}>{goal.type}</span>
          {goal.linkedAccount && <span className={css.goalAccount}>· {goal.linkedAccount}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp2)' }}>
          {statusLabel && (
            <span className={css.statusPill} data-status={status.status}>{statusLabel}</span>
          )}
          {goal.priority === 'high' && <div className={css.goalPriorityDot} />}
        </div>
      </div>
      {/* Row 2: progress bar */}
      <div className={css.goalBarTrack}>
        <div className={css.goalBarFill} style={{ width: `${pct}%`, background: barColor }} />
      </div>
      {/* Row 3: numbers */}
      <div className={css.goalNumbers}>
        <span className={css.goalProgressLabel}>{progressLabel}</span>
        <span className={css.goalPct} style={{ color: barColor }}>{pctLabel}</span>
      </div>
      {/* Row 4: target date + pace line */}
      {targetStr && !paceLine && (
        <div className={css.goalTargetDate}>target: {targetStr}</div>
      )}
      {paceLine && (
        <div className={css.goalPaceLine}>{paceLine}</div>
      )}
    </div>
  );
}

// Inline goal edit form (same expansion pattern as expense edit)
function GoalEditForm({ data, setData, onSave, onCancel, onDelete }) {
  return (
    <div className={css.inlineEdit}>
      <input className={css.editInput} value={data.title || ''} onChange={e => setData(p => ({ ...p, title: e.target.value }))} placeholder="goal title" autoFocus />
      {/* Type selector */}
      <div className={css.goalTypeToggle}>
        {GOAL_TYPES.map(t => (
          <button key={t} type="button"
            className={[css.goalTypeSeg, data.type === t ? css.goalTypeSegActive : ''].filter(Boolean).join(' ')}
            onClick={() => setData(p => ({ ...p, type: t }))}>
            {t}
          </button>
        ))}
      </div>
      <div className={css.formGrid2}>
        <input className={css.editInput} type="number" placeholder={data.type === 'debt-payoff' ? 'starting balance' : data.type === 'contribution' ? 'annual limit' : 'target amount'} value={data.targetAmount || ''} onChange={e => setData(p => ({ ...p, targetAmount: e.target.value }))} min="0" />
        <input className={css.editInput} type="number" placeholder={data.type === 'debt-payoff' ? 'current balance' : data.type === 'contribution' ? 'ytd contributed' : 'saved so far'} value={data.currentAmount || ''} onChange={e => setData(p => ({ ...p, currentAmount: e.target.value }))} min="0" />
      </div>
      <div className={css.formGrid2}>
        <input className={css.editInput} type="date" value={data.targetDate || ''} onChange={e => setData(p => ({ ...p, targetDate: e.target.value }))} />
        <SelectWrap>
          <select className={css.editSelect} value={data.priority || 'normal'} onChange={e => setData(p => ({ ...p, priority: e.target.value }))}>
            <option value="high">high</option>
            <option value="normal">normal</option>
            <option value="low">low</option>
          </select>
        </SelectWrap>
      </div>
      <input className={css.editInput} placeholder="linked account (optional)" value={data.linkedAccount || ''} onChange={e => setData(p => ({ ...p, linkedAccount: e.target.value }))} />
      <div className={css.editActions}>
        <button className={css.editSave} onClick={onSave}>save</button>
        <button className={css.editCancel} onClick={onCancel}>cancel</button>
        <button className={css.editDelete} onClick={onDelete}>delete</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FinancePage
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_EXPENSE_FORM = { name: '', amount: '', freq: 'monthly', next: '', category: '', isSubscription: false };
const EMPTY_INCOME_FORM  = { name: '', amount: '', freq: 'biweekly', nextDate: '', pretax: '', pretaxType: 'fixed', roth: '', rothType: 'fixed' };

export default function FinancePage({
  finance=[], income=[], budgets=[], categories=[], showAdd, setShowAdd, setSt, notify, st={},
}) {
  
  const [financeView,  setFinanceView]  = useState('unified');
  const [goalFilter,   setGoalFilter]   = useState(null); // null | 'on-track' | 'behind'

  // Clear goal filter when navigating away from goals view
  useEffect(() => {
    if (financeView !== 'goals') setGoalFilter(null);
  }, [financeView]);
  const [showIncAdd,   setShowIncAdd]   = useState(false);
  const [showCatMgr,   setShowCatMgr]   = useState(false);
  const [showAddGoal,  setShowAddGoal]  = useState(false);
  const [expForm,      setExpForm]      = useState(EMPTY_EXPENSE_FORM);
  const [incForm,      setIncForm]      = useState(EMPTY_INCOME_FORM);
  const [goalForm,     setGoalForm]     = useState(EMPTY_GOAL_FORM);
  const [editItem,     setEditItem]     = useState(null);
  const [editData,     setEditData]     = useState({});
  const [editGoal,     setEditGoal]     = useState(null); // goal id
  const [editGoalData, setEditGoalData] = useState({});
  const [catInput,     setCatInput]     = useState('');
  const [renamingCat,  setRenamingCat]  = useState(null);
  const [renameVal,    setRenameVal]    = useState('');
  const addExpRef = useRef(null);
  const addIncRef = useRef(null);
  const addGoalRef = useRef(null);

  const allCats = useMemo(() =>
    (categories && categories.length > 0) ? categories : DEFAULT_CATS, [categories]);

  const goals = useMemo(() => st.goals || [], [st.goals]);


  // ── Transaction ledger ──────────────────────────────────────────────────────
  const transactions = useMemo(() => st.transactions || [], [st.transactions]);
  const [showTxAdd, setShowTxAdd] = useState(false);
  const [txForm, setTxForm] = useState({ amount: '', category: '', note: '', date: TODAY });

  const txCategories = useMemo(() => {
    const base = ['food','transport','entertainment','health','shopping','utilities','other'];
    return [...new Set([...base, ...allCats.map(c => c.toLowerCase())])];
  }, [allCats]);

  const weekStart = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay());
    return d.toISOString().slice(0, 10);
  }, []);

  const monthStart = useMemo(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-01';
  }, []);

  const weeklySpend = useMemo(() =>
    (st.transactions || []).filter(t => t.date >= weekStart)
      .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0),
    [st.transactions, weekStart]);

  const monthlyTxSpend = useMemo(() =>
    (st.transactions || []).filter(t => t.date >= monthStart)
      .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0),
    [st.transactions, monthStart]);

  const spendByCategory = useMemo(() => {
    const map = {};
    (st.transactions || []).filter(t => t.date >= monthStart).forEach(t => {
      const c = (t.category || 'other').toLowerCase();
      map[c] = (map[c] || 0) + (parseFloat(t.amount) || 0);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [st.transactions, monthStart]);

  const addTransaction = () => {
    if (!txForm.amount || !txForm.category) return;
    const tx = {
      id: 'tx' + Date.now(),
      amount: parseFloat(txForm.amount),
      category: txForm.category.toLowerCase(),
      note: txForm.note,
      date: txForm.date || TODAY,
      source: 'manual',
    };
    setSt(p => ({ ...p, transactions: [tx, ...(p.transactions || [])] }));
    setTxForm({ amount: '', category: '', note: '', date: TODAY });
    setShowTxAdd(false);
    notify('Logged');
  };

  const deleteTx = id =>
    setSt(p => ({ ...p, transactions: (p.transactions || []).filter(t => t.id !== id) }));


  // ── Investment accounts ─────────────────────────────────────────────────────
  const accounts = useMemo(() => st.accounts || [], [st.accounts]);
  const [showAccAdd, setShowAccAdd] = useState(false);
  const [accForm, setAccForm] = useState({ name: '', type: '401k', balance: '', note: '' });
  const ACC_TYPES = ['401k', 'Roth IRA', 'Traditional IRA', 'Brokerage', 'HSA', 'Crypto', 'Other'];
  const totalPortfolio = useMemo(() =>
    accounts.reduce((s, a) => s + (parseFloat(a.balance) || 0), 0), [accounts]);
  const addAccount = () => {
    if (!accForm.name || !accForm.balance) return;
    const acc = {
      id: 'acc' + Date.now(), name: accForm.name, type: accForm.type,
      balance: parseFloat(accForm.balance), note: accForm.note, updatedAt: TODAY,
      history: [{ date: TODAY, balance: parseFloat(accForm.balance) }],
    };
    setSt(p => ({ ...p, accounts: [...(p.accounts || []), acc] }));
    setAccForm({ name: '', type: '401k', balance: '', note: '' });
    setShowAccAdd(false);
    notify(acc.name + ' added');
  };
  const updateAccountBalance = (id, newBalance) => {
    setSt(p => ({
      ...p,
      accounts: (p.accounts || []).map(a => {
        if (a.id !== id) return a;
        const bal = parseFloat(newBalance) || 0;
        const history = [...(a.history || []), { date: TODAY, balance: bal }]
          .filter((h, i, arr) => arr.findIndex(x => x.date === h.date) === i)
          .sort((a, b) => a.date.localeCompare(b.date)).slice(-24);
        return { ...a, balance: bal, updatedAt: TODAY, history };
      }),
    }));
  };
  const deleteAccount = id =>
    setSt(p => ({ ...p, accounts: (p.accounts || []).filter(a => a.id !== id) }));

  // ── Projection engine ────────────────────────────────────────────────────────
  const [currentAge, setCurrentAge] = useState(() => {
    try { return parseInt(localStorage.getItem('mylo_proj_age') || '0', 10) || null; } catch { return null; }
  });

  const [projTarget,    setProjTarget]    = useState(() => { try { return parseFloat(localStorage.getItem('mylo_proj_target') || '3000000'); } catch { return 3000000; } });
  const [projRetireAge, setProjRetireAge] = useState(() => { try { return parseInt(localStorage.getItem('mylo_proj_retire') || '42', 10); } catch { return 42; } });
  const [projMonthly,   setProjMonthly]   = useState(() => { try { return parseFloat(localStorage.getItem('mylo_proj_monthly') || '1200'); } catch { return 1200; } });
  const [projReturn,    setProjReturn]    = useState(7);

  const saveProjSetting = (key, val) => { try { localStorage.setItem(key, String(val)); } catch {} };

  const projection = useMemo(() => {
    const pv  = totalPortfolio;
    const pmt = projMonthly;
    const r   = (projReturn / 100) / 12;
    const yearsToRetire = currentAge ? Math.max(0, projRetireAge - currentAge) : projRetireAge;
    const n   = yearsToRetire * 12;
    const fvAtRetire = r > 0
      ? pv * Math.pow(1+r,n) + pmt * ((Math.pow(1+r,n)-1)/r)
      : pv + pmt * n;
    let yearsToTarget = null;
    if (r > 0) {
      for (let mo = 1; mo <= 720; mo++) {
        const fv = pv * Math.pow(1+r,mo) + pmt * ((Math.pow(1+r,mo)-1)/r);
        if (fv >= projTarget) { yearsToTarget = mo/12; break; }
      }
    }
    let monthlyNeeded = null;
    if (r > 0 && n > 0) {
      const factor = (Math.pow(1+r,n)-1)/r;
      monthlyNeeded = factor > 0 ? (projTarget - pv * Math.pow(1+r,n)) / factor : null;
      if (monthlyNeeded !== null && monthlyNeeded < 0) monthlyNeeded = 0;
    }
    const snapshots = [5,10,20].map(yrs => {
      const mo = yrs*12;
      const fv = r>0 ? pv*Math.pow(1+r,mo)+pmt*((Math.pow(1+r,mo)-1)/r) : pv+pmt*mo;
      return { yrs, fv };
    });
    return { fvAtRetire, yearsToRetire, yearsToTarget, monthlyNeeded, onTrack: fvAtRetire >= projTarget, gap: projTarget - fvAtRetire, snapshots };
  }, [totalPortfolio, projMonthly, projReturn, projTarget, projRetireAge, currentAge]);


  // ── One-time destructive migration ────────────────────────────────────────
  useEffect(() => {
    const migrated = (() => { try { return localStorage.getItem('finance.migrated.v2') === 'true'; } catch { return false; } })();
    if (migrated) return;
    const oldFields = ['hsa','hsaType','roth401k','roth401kType','traditional401k','traditional401kType','other','otherType'];
    const incomeArr = st.income || [];
    const needsMigration = incomeArr.some(i => oldFields.some(f => f in i));
    if (!needsMigration) { try { localStorage.setItem('finance.migrated.v2', 'true'); } catch {} return; }
    setSt(p => ({
      ...p,
      income: (p.income || []).map(i => {
        const clean = { ...i };
        oldFields.forEach(f => delete clean[f]);
        clean.amount = 0; clean.pretax = 0; clean.pretaxType = 'fixed';
        clean.roth = 0; clean.rothType = 'fixed';
        return clean;
      }),
    }));
    try { localStorage.setItem('finance.migrated.v2', 'true'); } catch {}
    notify('income simplified — please re-enter your paycheck details');
  }, []); // eslint-disable-line

  // ── Computed values ───────────────────────────────────────────────────────
  const financeArr = finance || [];
  const incomeArr  = income  || [];

  const totalExpenses = useMemo(() =>
    financeArr.reduce((s, e) => s + monthlyEquiv(parseFloat(e.amount || 0), e.freq), 0), [financeArr]);
  const totalIncome = useMemo(() =>
    incomeArr.reduce((s, i) => s + monthlyEquiv(parseFloat(i.amount || 0), i.freq), 0), [incomeArr]);
  const totalDeductions = useMemo(() =>
    incomeArr.reduce((s, i) => s + monthlyEquiv(calcDeduction(i, 'pretax') + calcDeduction(i, 'roth'), i.freq), 0), [incomeArr]);
  const netMonthly = totalIncome - totalDeductions - totalExpenses;

  const dueSoon = useMemo(() =>
    financeArr.filter(e => { if (!e.next) return false; const d = df(e.next); return d >= 0 && d <= 14; }).length, [financeArr]);

  // ── Sorted goals ─────────────────────────────────────────────────────────
  const sortedGoals = useMemo(() => {
    const order = { high: 0, normal: 1, low: 2 };
    return [...goals].sort((a, b) =>
      (order[a.priority] - order[b.priority]) ||
      (new Date(a.createdAt) - new Date(b.createdAt))
    );
  }, [goals]);

  // Filtered goals for goals view (does not affect unified view)
  const visibleGoals = useMemo(() => {
    if (!goalFilter) return sortedGoals;
    return sortedGoals.filter(g => {
      const s = computeGoalStatus(g).status;
      if (goalFilter === 'on-track') return s === 'ahead' || s === 'on-track';
      if (goalFilter === 'behind')   return s === 'behind' || s === 'at-risk';
      return true;
    });
  }, [sortedGoals, goalFilter]);

  // ── CRUD — expenses ───────────────────────────────────────────────────────
  const handleAdd = () => {
    if (!expForm.name.trim() || !expForm.amount) return;
    const item = {
      id: `f${Date.now()}`, name: expForm.name.trim(),
      amount: parseFloat(expForm.amount), freq: expForm.freq,
      next: expForm.next || '',
      
      category: allCats.includes(expForm.category) ? expForm.category : (expForm.category || 'Other'),
      isSubscription: expForm.isSubscription || false,
    };
    setSt(p => ({ ...p, finance: [...(p.finance || []), item] }));
    setExpForm(EMPTY_EXPENSE_FORM); setShowAdd(false); notify('Expense added');
  };

  const handleAddIncome = () => {
    if (!incForm.name.trim() || !incForm.amount) return;
    const item = {
      id: `i${Date.now()}`, name: incForm.name.trim(),
      amount: parseFloat(incForm.amount), freq: incForm.freq,
      nextDate: incForm.nextDate || '',
      pretax: parseFloat(incForm.pretax || 0), pretaxType: incForm.pretaxType,
      roth: parseFloat(incForm.roth || 0), rothType: incForm.rothType,
    };
    setSt(p => ({ ...p, income: [...(p.income || []), item] }));
    setIncForm(EMPTY_INCOME_FORM); setShowIncAdd(false); notify('Income source added');
  };

  // ── CRUD — goals ──────────────────────────────────────────────────────────
  const addGoal = () => {
    if (!goalForm.title.trim() || !goalForm.targetAmount || !goalForm.currentAmount) return;
    const item = {
      id: `g${Date.now()}`,
      title: goalForm.title.trim(),
      type: goalForm.type,
      targetAmount: parseFloat(goalForm.targetAmount),
      currentAmount: parseFloat(goalForm.currentAmount),
      targetDate: goalForm.targetDate || null,
      linkedAccount: goalForm.linkedAccount.trim() || null,
      priority: goalForm.priority,
      createdAt: new Date().toISOString(),
    };
    setSt(p => ({ ...p, goals: [...(p.goals || []), item] }));
    resetGoalForm(); setShowAddGoal(false); notify('Goal added');
  };

  const updateGoal = () => {
    if (!editGoal) return;
    setSt(p => ({
      ...p,
      goals: (p.goals || []).map(g => g.id === editGoal ? {
        ...g, ...editGoalData,
        targetAmount: parseFloat(editGoalData.targetAmount),
        currentAmount: parseFloat(editGoalData.currentAmount),
        targetDate: editGoalData.targetDate || null,
        linkedAccount: (editGoalData.linkedAccount || '').trim() || null,
      } : g),
    }));
    setEditGoal(null); notify('Goal updated');
  };

  const deleteGoal = id => {
    setSt(p => ({ ...p, goals: (p.goals || []).filter(g => g.id !== id) }));
    setEditGoal(null); notify('Goal removed');
  };

  const resetGoalForm = () => setGoalForm(EMPTY_GOAL_FORM);

  const deleteFinance = id => {
    setSt(p => ({ ...p, finance: (p.finance || []).filter(e => e.id !== id) }));
    setEditItem(null); notify('Expense removed');
  };

  const deleteIncome = id => {
    setSt(p => ({ ...p, income: (p.income || []).filter(i => i.id !== id) }));
    setEditItem(null); notify('Income source removed');
  };

  const saveEditItem = () => {
    if (!editItem) return;
    if (editItem.type === 'expense') {
      setSt(p => ({ ...p, finance: (p.finance || []).map(e => e.id === editItem.id ? { ...e, ...editData } : e) }));
    } else {
      setSt(p => ({ ...p, income: (p.income || []).map(i => i.id === editItem.id ? { ...i, ...editData } : i) }));
    }
    setEditItem(null); notify('Saved');
  };

  const openEdit  = (type, item) => { setEditItem({ type, id: item.id }); setEditData({ ...item }); };
  const isEditing = (type, id)   => editItem?.type === type && editItem?.id === id;

  const addCategory = () => {
    if (!catInput.trim()) return;
    setSt(p => ({ ...p, financeCategories: [...(p.financeCategories || DEFAULT_CATS), catInput.trim()] }));
    setCatInput(''); notify('Category added');
  };

  const deleteCategory = cat => {
    if (allCats.length <= 1) return;
    setSt(p => ({ ...p, financeCategories: (p.financeCategories || DEFAULT_CATS).filter(c => c !== cat) }));
  };

  const renameCategory = (oldCat, newCat) => {
    if (!newCat.trim() || newCat === oldCat) { setRenamingCat(null); return; }
    setSt(p => ({
      ...p,
      financeCategories: (p.financeCategories || DEFAULT_CATS).map(c => c === oldCat ? newCat.trim() : c),
      finance: (p.finance || []).map(e => e.category === oldCat ? { ...e, category: newCat.trim() } : e),
    }));
    setRenamingCat(null); notify('Category renamed');
  };

  
  const normalizeItemCat = (item) =>
    allCats.includes(item.category) ? item.category : 'Other';
  const handleAddBtn = () => {
    if (financeView === 'earn')  { setShowIncAdd(true);  setTimeout(() => addIncRef.current?.focus(), 50); }
    else if (financeView === 'goals') { setShowAddGoal(true); setTimeout(() => addGoalRef.current?.focus(), 50); }
    else if (financeView === 'invest') { setShowAccAdd(true); }
    else { setShowAdd(true); setTimeout(() => addExpRef.current?.focus(), 50); }
  };

  const addBtnLabel = financeView === 'earn' ? 'income' : financeView === 'goals' ? 'goal' : financeView === 'invest' ? 'account' : financeView === 'ledger' ? 'entry' : 'expense';

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={css.page}>

      {/* ── Header: title row + controls row ── */}
      <div className={css.pageHdr}>
        <div className={css.pageHdrTop}>
          <CyclingTitle
            pageLabel="finance"
            filters={FINANCE_FILTERS}
            labels={['overview','ledger','expenses','income','invest','goals']}
            value={financeView}
            onChange={v => { setFinanceView(v); try { document.querySelector('.main')?.scrollTo(0,0); } catch{} }}
          />
          <button className={css.addBtn} onClick={handleAddBtn}>
            <Plus size={13} strokeWidth={2} />{addBtnLabel}
          </button>
        </div>
        <div style={{width:'100%',display:'flex',gap:5,alignItems:'center',paddingTop:6,paddingBottom:2}}>
          {FINANCE_FILTERS.map((f,i) => (
            <div key={f}
              style={{
                width: financeView===f ? 14 : 5,
                height: 5,
                borderRadius: 3,
                background: financeView===f ? 'var(--accent)' : 'var(--b2)',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
                flexShrink: 0,
                pointerEvents: 'none',
              }}/>
          ))}
        </div>
      </div>

      {/* ── Pulse — goals view shows goal stats, other views show net/income/expenses ── */}
      {financeView === 'goals' ? (() => {
        const allGoals = sortedGoals;
        const statuses = allGoals.map(g => computeGoalStatus(g));
        const activeCount  = statuses.filter(s => s.status !== 'complete').length;
        const onTrackCount = statuses.filter(s => s.status === 'ahead' || s.status === 'on-track').length;
        const behindCount  = statuses.filter(s => s.status === 'behind' || s.status === 'at-risk').length;
        const hasAtRisk    = statuses.some(s => s.status === 'at-risk');
        const onTrackColor = onTrackCount === 0 && activeCount > 0 ? 'var(--accent-d)' : 'var(--accent-g)';
        const behindColor  = behindCount > 0 ? 'var(--accent-d)' : 'var(--ink)';
        return (
          <div className={css.pulse}>
            <PulseCell number={allGoals.length === 0 ? '—' : activeCount} label="active" variant="large" />
            <PulseCell
              number={allGoals.length === 0 ? '—' : onTrackCount}
              label="on track"
              numberColor={onTrackCount > 0 ? onTrackColor : undefined}
              isActive={goalFilter === 'on-track'}
              onClick={() => setGoalFilter(goalFilter === 'on-track' ? null : 'on-track')}
              ariaLabel={goalFilter === 'on-track' ? 'Clear on-track filter' : 'Filter to on-track goals'}
            />
            <PulseCell
              number={allGoals.length === 0 ? '—' : behindCount}
              label={hasAtRisk ? '⚠ behind' : 'behind'}
              numberColor={behindColor}
              isActive={goalFilter === 'behind'}
              onClick={() => setGoalFilter(goalFilter === 'behind' ? null : 'behind')}
              ariaLabel={goalFilter === 'behind' ? 'Clear behind filter' : 'Filter to behind goals'}
            />
          </div>
        );
      })() : (
        <div className={css.pulse}>
          <PulseCell
            number={`${netMonthly >= 0 ? '+' : '-'}${fmtCurrency(netMonthly)}`}
            label="net / mo"
            variant="large"
            numberColor={netMonthly >= 0 ? 'var(--accent-g)' : 'var(--accent-d)'}
            largeNumber
          />
          <PulseCell number={fmtCurrency(totalIncome)}   label="income"   isActive={financeView === 'earn'}   onClick={() => setFinanceView(financeView === 'earn' ? 'unified' : 'earn')} ariaLabel={financeView === 'earn' ? 'View all' : 'View income'} />
          <PulseCell number={fmtCurrency(totalExpenses)} label="expenses" isActive={financeView === 'spend'} onClick={() => setFinanceView(financeView === 'spend' ? 'unified' : 'spend')} ariaLabel={financeView === 'spend' ? 'View all' : 'View expenses'} />
        </div>
      )}


      {dueSoon >= 2 && (
        <div className={css.dueSoon}>
          <Bell size={12} strokeWidth={1.5} style={{ flexShrink: 0 }} />
          <span>{dueSoon} payment{dueSoon > 1 ? 's' : ''} due in the next 14 days</span>
        </div>
      )}

      {/* ── Category manager ── */}
      {showCatMgr && (
        <div className={css.catMgr}>
          <div className={css.catMgrTitle}>categories</div>
          {allCats.map(cat => (
            <div key={cat} className={css.catRow}>
              {renamingCat === cat ? (
                <input className={css.catRenameInput} value={renameVal}
                  onChange={e => setRenameVal(e.target.value)}
                  onBlur={() => renameCategory(cat, renameVal)}
                  onKeyDown={e => { if (e.key === 'Enter') renameCategory(cat, renameVal); if (e.key === 'Escape') setRenamingCat(null); }}
                  autoFocus />
              ) : (
                <button className={css.catName} onClick={() => { setRenamingCat(cat); setRenameVal(cat); }}>{cat}</button>
              )}
              <button className={css.catDelete} onClick={() => deleteCategory(cat)} disabled={allCats.length <= 1}>
                <X size={13} strokeWidth={1.5} />
              </button>
            </div>
          ))}
          <div className={css.catAddRow}>
            <input className={css.catAddInput} placeholder="add category..." value={catInput}
              onChange={e => setCatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCategory()} />
            <button className={css.catAddBtn} onClick={addCategory}>+</button>
          </div>
        </div>
      )}

      {/* ══════════════ UNIFIED VIEW ══════════════ */}
      {financeView === 'unified' && (
        <div className={css.unifiedStack}>
          <div className={css.unifiedSection}>
            <SectionHeader label="expenses" meta={`${financeArr.length}`} />
            <div className={css.sectionCard}>
              {financeArr.length === 0
                ? <EmptyState message="no expenses yet" cta={{ label:"+ add your first expense", onClick:() => { setFinanceView('expenses'); setShowAdd(true); } }} />
                : allCats.map(cat => {
                    
                    const items = financeArr.filter(e => normalizeItemCat(e) === cat);
                    if (items.length === 0) return null;
                    return (
                      <div key={cat} className={css.groupWrap}>
                        <div className={css.catHdr}>{cat.toLowerCase()}</div>
                        <div className={css.itemList}>
                          {items.map((item, idx) => {
                            const dueStr = item.next ? relDue(item.next) : null;
                            const urgent = item.next && df(item.next) >= 0 && df(item.next) <= 7;
                            const metaParts = [urgent ? 'due today' : dueStr, freqLabel(item.freq)].filter(Boolean);
                            return (
                              <div key={item.id} className={[css.itemRow, idx === items.length - 1 ? css.itemRowLast : ''].filter(Boolean).join(' ')}>
                                <div className={css.itemBody}>
                                  {urgent && <Bell size={13} strokeWidth={1.5} className={css.urgentBell} />}
                                  <span className={css.itemName}>{item.name}</span>
                                  {metaParts.length > 0 && <span className={css.itemMetaInline}>· {metaParts.join(' · ')}</span>}
                                </div>
                                <div className={css.itemAmount}>
                                  <span className={css.itemAmountValue}>{fmtCurrency(item.amount)}</span>
                                  <span className={css.itemAmountFreq}>/{freqLabel(item.freq)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
              }
            </div>
          </div>

          <div className={css.unifiedSection}>
            <SectionHeader label="income" meta={`${incomeArr.length}`} />
            <div className={css.sectionCard}>
              {incomeArr.length === 0
                ? <EmptyState message="no income sources set" cta={{ label:"+ add your first income source", onClick:() => { setFinanceView('income'); setShowIncAdd(true); } }} />
                : incomeArr.map(inc => {
                    const pretaxAmt = calcDeduction(inc, 'pretax');
                    const rothAmt   = calcDeduction(inc, 'roth');
                    const net       = parseFloat(inc.amount || 0) - pretaxAmt - rothAmt;
                    const nextStr   = inc.nextDate ? relDue(inc.nextDate) : null;
                    return (
                      <div key={inc.id} style={{ borderBottom: '1px solid var(--b1)' }}>
                        <div className={css.incomeRow}>
                          <div className={css.itemBody}>
                            <div className={css.itemName}>{inc.name}</div>
                            {nextStr && <div className={css.itemMeta}>next: {nextStr}</div>}
                          </div>
                          <div className={css.itemAmount}>
                            <span className={css.itemAmountValue}>{fmtCurrency(inc.amount)}</span>
                            <span className={css.itemAmountFreq}>/{freqLabel(inc.freq)}</span>
                          </div>
                        </div>
                        {(pretaxAmt > 0 || rothAmt > 0) && (
                          <div className={css.deductionRows}>
                            {pretaxAmt > 0 && <SignalRow label="pretax" value={fmtCurrency(pretaxAmt)} accent="soft" />}
                            {rothAmt   > 0 && <SignalRow label="roth"   value={fmtCurrency(rothAmt)}   accent="soft" />}
                            <SignalRow label="net paycheck" value={fmtCurrency(net)} accent="gold" />
                          </div>
                        )}
                      </div>
                    );
                  })
              }
            </div>
          </div>

          <div className={css.unifiedSection}>
            <SectionHeader label="goals" meta={`${sortedGoals.length}`} />
            <div className={css.sectionCard}>
              {sortedGoals.length === 0
                ? <EmptyState message="no goals set" cta={{ label:"+ add your first goal", onClick:() => { setFinanceView('goals'); setShowAddGoal(true); } }} />
                : sortedGoals.map(goal => (
                    <GoalCard key={goal.id} goal={goal} onEdit={() => { setFinanceView('goals'); setEditGoal(goal.id); setEditGoalData({ ...goal, targetAmount: String(goal.targetAmount), currentAmount: String(goal.currentAmount), targetDate: goal.targetDate || '', linkedAccount: goal.linkedAccount || '' }); }} />
                  ))
              }
            </div>
          </div>
        </div>
      )}


      {financeView === 'ledger' && (
        <div style={{padding:'0 0 var(--sp12)'}}>
          <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',padding:'18px 20px',marginBottom:8}}>
            <div style={{fontFamily:'var(--sans)',fontSize:36,fontWeight:300,color:'var(--ink)',letterSpacing:'-0.6px',lineHeight:1}}>{fmtCurrency(weeklySpend)}</div>
            <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)',marginTop:4,letterSpacing:'0.03em'}}>spent this week</div>
          </div>
          {showTxAdd ? (
            <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',padding:'14px 16px',marginBottom:8}}>
              <div style={{display:'flex',gap:8,marginBottom:8}}>
                <input type="number" inputMode="decimal" placeholder="amount" value={txForm.amount}
                  onChange={e => setTxForm(p => ({...p, amount: e.target.value}))} autoFocus
                  style={{flex:'0 0 100px',height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:16,color:'var(--ink)',outline:'none'}}/>
                <select value={txForm.category} onChange={e => setTxForm(p => ({...p, category: e.target.value}))}
                  style={{flex:1,height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:16,color:txForm.category?'var(--ink)':'var(--t4)',outline:'none',appearance:'none'}}>
                  <option value="">category</option>
                  {txCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{display:'flex',gap:8}}>
                <input placeholder="note (optional)" value={txForm.note}
                  onChange={e => setTxForm(p => ({...p, note: e.target.value}))}
                  onKeyDown={e => { if (e.key === 'Enter') addTransaction(); }}
                  style={{flex:1,height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:16,color:'var(--ink)',outline:'none'}}/>
                <input type="date" value={txForm.date} onChange={e => setTxForm(p => ({...p, date: e.target.value}))}
                  style={{width:130,height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 8px',fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',outline:'none'}}/>
              </div>
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <button onClick={addTransaction} disabled={!txForm.amount || !txForm.category}
                  style={{flex:1,height:44,background:'var(--accent)',border:'none',borderRadius:'var(--r2)',fontFamily:'var(--sans)',fontSize:13,fontWeight:500,color:'var(--on-accent)',cursor:'pointer',opacity:(!txForm.amount||!txForm.category)?0.4:1}}>log</button>
                <button onClick={() => setShowTxAdd(false)}
                  style={{height:44,padding:'0 16px',background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',fontFamily:'var(--sans)',fontSize:13,color:'var(--t2)',cursor:'pointer'}}>cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowTxAdd(true)}
              style={{width:'100%',height:44,background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',fontFamily:'var(--sans)',fontSize:13,color:'var(--accent)',cursor:'pointer',marginBottom:8}}>
              + log spend
            </button>
          )}
          {spendByCategory.length > 0 && (
            <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',marginBottom:8,overflow:'hidden'}}>
              <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)',letterSpacing:'0.04em',padding:'12px 16px 8px'}}>this month · {fmtCurrency(monthlyTxSpend)}</div>
              {spendByCategory.map(([cat, amt]) => {
                const pct = monthlyTxSpend > 0 ? (amt / monthlyTxSpend) * 100 : 0;
                return (
                  <div key={cat} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 16px',borderTop:'0.5px solid var(--b1)'}}>
                    <div style={{fontFamily:'var(--sans)',fontSize:12,color:'var(--t2)',width:90,flexShrink:0}}>{cat}</div>
                    <div style={{flex:1,height:4,background:'var(--b2)',borderRadius:2,overflow:'hidden'}}>
                      <div style={{height:'100%',width:pct + '%',background:'var(--accent)',borderRadius:2}}/>
                    </div>
                    <div style={{fontFamily:'var(--sans)',fontSize:12,color:'var(--t2)',width:52,textAlign:'right',flexShrink:0}}>{fmtCurrency(amt)}</div>
                  </div>
                );
              })}
            </div>
          )}
          {(st.transactions || []).length === 0 ? (
            <div style={{textAlign:'center',padding:'40px 0',fontFamily:'var(--sans)',fontSize:13,color:'var(--t4)'}}>no transactions yet</div>
          ) : (
            <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',overflow:'hidden'}}>
              <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)',letterSpacing:'0.04em',padding:'12px 16px 8px'}}>all transactions</div>
              {(st.transactions || []).slice(0, 60).map(tx => (
                <div key={tx.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderTop:'0.5px solid var(--b1)'}}>
                  <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t4)',flexShrink:0,width:36}}>{tx.date ? tx.date.slice(5).replace('-','/') : '—'}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontFamily:'var(--sans)',fontSize:13,color:'var(--ink)'}}>{tx.category}</div>
                    {tx.note && <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)'}}>{tx.note}</div>}
                  </div>
                  <div style={{fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',flexShrink:0}}>{fmtCurrency(parseFloat(tx.amount) || 0)}</div>
                  <button onClick={() => deleteTx(tx.id)}
                    style={{background:'transparent',border:'none',color:'var(--t4)',cursor:'pointer',fontSize:16,padding:'4px 8px',flexShrink:0,minWidth:32,textAlign:'center'}}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ EXPENSES ══════════════ */}
      {(financeView === 'spend' || financeView === 'unified' || financeView === 'ledger') && (
        <>
          {showAdd && (
            <div className={css.addCard}>
              <input ref={addExpRef} className={css.addInput} placeholder="expense name"
                value={expForm.name} onChange={e => setExpForm(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleAdd()} autoComplete="off" />
              <div className={css.formGrid2}>
                <input className={css.addInput} type="number" placeholder="amount" value={expForm.amount}
                  onChange={e => setExpForm(p => ({ ...p, amount: e.target.value }))} min="0" />
                <SelectWrap>
                  <select className={css.addSelect} value={expForm.freq} onChange={e => setExpForm(p => ({ ...p, freq: e.target.value }))}>
                    {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </SelectWrap>
              </div>
              <div className={css.formGrid2}>
                <input className={css.addInput} type="date" value={expForm.next}
                  onChange={e => setExpForm(p => ({ ...p, next: e.target.value }))} />
                <SelectWrap>
                  <select className={css.addSelect} value={expForm.category} onChange={e => setExpForm(p => ({ ...p, category: e.target.value }))}>
                    <option value="">category</option>
                    {allCats.map(c => <option key={c} value={c}>{c.toLowerCase()}</option>)}
                  </select>
                </SelectWrap>
              </div>
              <div className={css.toggleRow} onClick={() => setExpForm(p => ({ ...p, isSubscription: !p.isSubscription }))}>
                <div className={[css.toggleSwitch, expForm.isSubscription ? css.toggleSwitchOn : ''].filter(Boolean).join(' ')}>
                  <div className={css.toggleKnob} />
                </div>
                <span className={css.checkLabel}>this is a subscription</span>
              </div>
              <button className={[css.submitBtn, expForm.name.trim() && expForm.amount ? css.submitBtnActive : ''].filter(Boolean).join(' ')}
                onClick={handleAdd} disabled={!expForm.name.trim() || !expForm.amount}>add expense</button>
              <button className={css.cancelLink} onClick={() => { setShowAdd(false); setExpForm(EMPTY_EXPENSE_FORM); }}>cancel</button>
            </div>
          )}

          {financeArr.length === 0 ? (
            <div className={css.sectionCard}>
              <EmptyState message="no expenses yet" cta={{ label:"+ add your first expense", onClick:() => { setShowAdd(true); setTimeout(() => addExpRef.current?.focus(), 50); } }} />
            </div>
          ) : (
            
            
            <div className={css.sectionCard}>
              {allCats.map(cat => {
                const items = financeArr.filter(e => normalizeItemCat(e) === cat);
                if (items.length === 0) return null;
                return (
                  <div key={cat} className={css.groupWrap}>
                    <div className={css.catHdr}>{cat.toLowerCase()}</div>
                    <div className={css.itemList}>
                      {items.map((item, idx) => {
                        const editing = isEditing('expense', item.id);
                        const dueStr  = item.next ? relDue(item.next) : null;
                        const urgent  = item.next && df(item.next) >= 0 && df(item.next) <= 7;
                        
                        const metaParts = [
                          urgent ? 'due today' : dueStr,
                          freqLabel(item.freq),
                        ].filter(Boolean);
                        return (
                          <div key={item.id}>
                            <div className={[css.itemRow, idx === items.length - 1 && !editing ? css.itemRowLast : ''].filter(Boolean).join(' ')}
                              onClick={() => !editing && openEdit('expense', item)}>
                              <div className={css.itemBody}>
                                {urgent && <Bell size={13} strokeWidth={1.5} className={css.urgentBell} />}
                                <span className={css.itemName}>{item.name}</span>
                                {metaParts.length > 0 && (
                                  <span className={css.itemMetaInline}>· {metaParts.join(' · ')}</span>
                                )}
                              </div>
                              <div className={css.itemAmount}>
                                <span className={css.itemAmountValue}>{fmtCurrency(item.amount)}</span>
                                <span className={css.itemAmountFreq}>/{freqLabel(item.freq)}</span>
                              </div>
                            </div>
                            {editing && <InlineExpenseEdit editData={editData} setEditData={setEditData}
                              allCats={allCats} onSave={saveEditItem} onCancel={() => setEditItem(null)}
                              onDelete={() => deleteFinance(item.id)} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ══════════════ INCOME ══════════════ */}
      {financeView === 'earn' && (
        <>
          {showIncAdd && (
            <div className={css.addCard}>
              <input ref={addIncRef} className={css.addInput} placeholder="income source name"
                value={incForm.name} onChange={e => setIncForm(p => ({ ...p, name: e.target.value }))} autoComplete="off" />
              <div className={css.formGrid2}>
                <input className={css.addInput} type="number" placeholder="amount" value={incForm.amount}
                  onChange={e => setIncForm(p => ({ ...p, amount: e.target.value }))} min="0" />
                <SelectWrap>
                  <select className={css.addSelect} value={incForm.freq} onChange={e => setIncForm(p => ({ ...p, freq: e.target.value }))}>
                    {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </SelectWrap>
              </div>
              <div className={css.fieldRow}>
                <div className={css.fieldLabel}>next payday</div>
                <input className={css.addInput} type="date" value={incForm.nextDate}
                  onChange={e => setIncForm(p => ({ ...p, nextDate: e.target.value }))} />
              </div>
              <div className={css.deductionSection}>
                <div className={css.deductionLabel}>deductions</div>
                <div className={css.formGrid2}>
                  <input className={css.addInput} type="number" placeholder="pretax" value={incForm.pretax}
                    onChange={e => setIncForm(p => ({ ...p, pretax: e.target.value }))} min="0" />
                  <TypeToggle value={incForm.pretaxType} onChange={v => setIncForm(p => ({ ...p, pretaxType: v }))} />
                </div>
                <div className={css.formGrid2}>
                  <input className={css.addInput} type="number" placeholder="roth" value={incForm.roth}
                    onChange={e => setIncForm(p => ({ ...p, roth: e.target.value }))} min="0" />
                  <TypeToggle value={incForm.rothType} onChange={v => setIncForm(p => ({ ...p, rothType: v }))} />
                </div>
              </div>
              <button className={[css.submitBtn, incForm.name.trim() && incForm.amount ? css.submitBtnActive : ''].filter(Boolean).join(' ')}
                onClick={handleAddIncome} disabled={!incForm.name.trim() || !incForm.amount}>add income source</button>
              <button className={css.cancelLink} onClick={() => { setShowIncAdd(false); setIncForm(EMPTY_INCOME_FORM); }}>cancel</button>
            </div>
          )}

          {incomeArr.length === 0 ? (
            <EmptyState message="no income sources set" cta={{ label:"+ add your first income source", onClick:() => { setShowIncAdd(true); setTimeout(() => addIncRef.current?.focus(), 50); } }} />
          ) : (
            incomeArr.map(inc => {
              const editing    = isEditing('income', inc.id);
              const pretaxAmt  = calcDeduction(inc, 'pretax');
              const rothAmt    = calcDeduction(inc, 'roth');
              const net        = parseFloat(inc.amount || 0) - pretaxAmt - rothAmt;
              const noDeductions = pretaxAmt === 0 && rothAmt === 0;
              const nextStr    = inc.nextDate ? relDue(inc.nextDate) : null;
              return (
                <div key={inc.id} className={css.incomeCard}>
                  <div className={css.incomeRow} onClick={() => !editing && openEdit('income', inc)}>
                    <div className={css.itemBody}>
                      <div className={css.itemName}>{inc.name}</div>
                      {nextStr && <div className={css.itemMeta}>next: {nextStr}</div>}
                    </div>
                    <div className={css.itemAmount}>
                      <span className={css.itemAmountValue}>{fmtCurrency(inc.amount)}</span>
                      <span className={css.itemAmountFreq}>/{freqLabel(inc.freq)}</span>
                    </div>
                  </div>
                  {!editing && (
                    <div className={css.deductionRows}>
                      {noDeductions ? (
                        <button className={css.addDeductionsLink} onClick={() => openEdit('income', inc)}>+ add deductions</button>
                      ) : (
                        <>
                          {pretaxAmt > 0 && <SignalRow label="pretax" value={fmtCurrency(pretaxAmt)} accent="soft" />}
                          {rothAmt > 0   && <SignalRow label="roth"   value={fmtCurrency(rothAmt)}   accent="soft" />}
                          <SignalRow label="net paycheck" value={fmtCurrency(net)} accent="gold" />
                        </>
                      )}
                    </div>
                  )}
                  {editing && (
                    <div className={css.inlineEdit}>
                      <input className={css.editInput} value={editData.name || ''} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} placeholder="name" autoFocus />
                      <div className={css.formGrid2}>
                        <input className={css.editInput} type="number" value={editData.amount || ''} onChange={e => setEditData(p => ({ ...p, amount: e.target.value }))} />
                        <SelectWrap><select className={css.editSelect} value={editData.freq || 'biweekly'} onChange={e => setEditData(p => ({ ...p, freq: e.target.value }))}>
                          {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select></SelectWrap>
                      </div>
                      <div className={css.fieldRow}>
                        <div className={css.fieldLabel}>next payday</div>
                        <input className={css.editInput} type="date" value={editData.nextDate || ''} onChange={e => setEditData(p => ({ ...p, nextDate: e.target.value }))} />
                      </div>
                      <div className={css.deductionSection}>
                        <div className={css.deductionLabel}>deductions</div>
                        <div className={css.formGrid2}>
                          <input className={css.editInput} type="number" placeholder="pretax" value={editData.pretax || ''} onChange={e => setEditData(p => ({ ...p, pretax: e.target.value }))} />
                          <TypeToggle value={editData.pretaxType || 'fixed'} onChange={v => setEditData(p => ({ ...p, pretaxType: v }))} />
                        </div>
                        <div className={css.formGrid2}>
                          <input className={css.editInput} type="number" placeholder="roth" value={editData.roth || ''} onChange={e => setEditData(p => ({ ...p, roth: e.target.value }))} />
                          <TypeToggle value={editData.rothType || 'fixed'} onChange={v => setEditData(p => ({ ...p, rothType: v }))} />
                        </div>
                      </div>
                      <div className={css.editActions}>
                        <button className={css.editSave} onClick={saveEditItem}>save</button>
                        <button className={css.editCancel} onClick={() => setEditItem(null)}>cancel</button>
                        <button className={css.editDelete} onClick={() => deleteIncome(inc.id)}>delete</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </>
      )}

      {/* ══════════════ GOALS ══════════════ */}
      
      {financeView === 'invest' && (
        <div style={{padding:'0 0 var(--sp12)'}}>
          {accounts.length === 0 ? (
            <div style={{textAlign:'center',padding:'var(--sp12) var(--sp6)',color:'var(--t3)'}}>
              <div style={{fontSize:28,opacity:.2,marginBottom:16}}>◇</div>
              <div style={{fontSize:14,fontWeight:500,color:'var(--t2)',marginBottom:8}}>no accounts yet</div>
              <div style={{fontSize:13,color:'var(--t3)',lineHeight:1.65,maxWidth:240,margin:'0 auto'}}>add your investment accounts to start projecting</div>
            </div>
          ) : (
            <div>
          {/* ── Projection Engine ── */}
          <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',overflow:'hidden',marginTop:8}}>

            <div style={{padding:'20px 16px 16px'}}>
              <div style={{fontSize:11,color:'var(--t3)',letterSpacing:'0.04em',marginBottom:14,fontFamily:'var(--sans)'}}>projection</div>
              <div style={{fontSize:38,fontWeight:300,color:'var(--ink)',letterSpacing:'-0.6px',lineHeight:1}}>{fmtCurrency(projection.fvAtRetire)}</div>
              <div style={{fontSize:12,color:'var(--t3)',marginTop:4,fontFamily:'var(--sans)'}}>
                at age {projRetireAge} · {projection.yearsToRetire}yr{projection.yearsToRetire!==1?'s':''} away
              </div>
              <div style={{display:'inline-block',fontSize:11,padding:'3px 10px',borderRadius:'var(--pill)',marginTop:12,
                background:projection.onTrack?'rgba(30,158,117,0.10)':'rgba(185,85,50,0.08)',
                color:projection.onTrack?'var(--accent-g)':'var(--accent-d)',
                border:'0.5px solid '+(projection.onTrack?'rgba(30,158,117,0.25)':'rgba(185,85,50,0.22)')}}>
                {projection.onTrack?'on track':'behind'}
              </div>
              {!projection.onTrack && projection.gap > 0 && (
                <div style={{fontSize:13,color:'var(--t2)',marginTop:8,fontFamily:'var(--sans)',lineHeight:1.5}}>
                  {fmtCurrency(projection.gap)} short
                  {projection.monthlyNeeded !== null && (
                    <span style={{color:'var(--accent)'}}> · need {fmtCurrency(projection.monthlyNeeded)}/mo</span>
                  )}
                </div>
              )}
              {projection.onTrack && (
                <div style={{fontSize:13,color:'var(--accent-g)',marginTop:8,fontFamily:'var(--sans)'}}>
                  {fmtCurrency(projection.fvAtRetire - projTarget)} surplus
                  {projection.yearsToTarget !== null && (
                    <span style={{color:'var(--t3)'}}> · hit target in {projection.yearsToTarget.toFixed(1)}yr</span>
                  )}
                </div>
              )}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,minmax(0,1fr))',gap:6,marginTop:14}}>
                {(projection.snapshots||[]).map(s => (
                  <div key={s.yrs} style={{background:'var(--s2)',borderRadius:'var(--r2)',padding:'10px 8px',textAlign:'center'}}>
                    <div style={{fontSize:14,fontWeight:300,color:'var(--ink)',letterSpacing:'-0.2px'}}>
                      {s.fv>=1000000?'$'+(s.fv/1000000).toFixed(1)+'M':fmtCurrency(s.fv)}
                    </div>
                    <div style={{fontSize:11,color:'var(--t4)',marginTop:2,fontFamily:'var(--sans)'}}>{s.yrs}yr</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{borderTop:'0.5px solid var(--b1)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px',borderBottom:'0.5px solid var(--b1)'}}>
                <div style={{fontSize:13,color:'var(--t2)',fontFamily:'var(--sans)'}}>target</div>
                <div style={{display:'flex',alignItems:'center',gap:4}}>
                  <span style={{fontSize:13,color:'var(--t3)',fontFamily:'var(--sans)'}}>$</span>
                  <input type="number" value={projTarget}
                    onChange={e=>{const v=parseFloat(e.target.value)||3000000;setProjTarget(v);saveProjSetting('mylo_proj_target',v);}}
                    onFocus={e=>e.target.style.borderColor='var(--accent)'}
                    onBlur={e=>e.target.style.borderColor='var(--b2)'}
                    style={{width:110,height:34,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',outline:'none',textAlign:'right'}}/>
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px',borderBottom:'0.5px solid var(--b1)'}}>
                <div style={{fontSize:13,color:'var(--t2)',fontFamily:'var(--sans)'}}>retire by age</div>
                <input type="number" value={projRetireAge}
                  onChange={e=>{const v=parseInt(e.target.value)||42;setProjRetireAge(v);saveProjSetting('mylo_proj_retire',v);}}
                  onFocus={e=>e.target.style.borderColor='var(--accent)'}
                  onBlur={e=>e.target.style.borderColor='var(--b2)'}
                  style={{width:70,height:34,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',outline:'none',textAlign:'right'}}/>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px',borderBottom:'0.5px solid var(--b1)'}}>
                <div style={{fontSize:13,color:'var(--t2)',fontFamily:'var(--sans)'}}>current age</div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  {currentAge ? (
                    <div style={{fontSize:14,color:'var(--ink)',minWidth:36,textAlign:'right',fontFamily:'var(--sans)'}}>{currentAge}</div>
                  ) : (
                    <input type="number" placeholder="age" value={typeof manualAge!=='undefined'?manualAge||'':currentAge||''}
                      onChange={e=>{const v=parseInt(e.target.value)||null;if(typeof setManualAge!=='undefined')setManualAge(v);else setCurrentAge(v);try{localStorage.setItem('mylo_proj_age',v||'');}catch{}}}
                      onFocus={e=>e.target.style.borderColor='var(--accent)'}
                      onBlur={e=>e.target.style.borderColor='var(--b2)'}
                      style={{width:70,height:34,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',outline:'none',textAlign:'right'}}/>
                  )}
                  {currentAge && <span style={{fontSize:12,color:'var(--t3)',fontFamily:'var(--sans)',flexShrink:0}}>{projRetireAge-currentAge}yr</span>}
                  {!currentAge && <span style={{fontSize:11,color:'var(--t4)',fontFamily:'var(--sans)'}}>set in profile →</span>}
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px',borderBottom:'0.5px solid var(--b1)'}}>
                <div style={{fontSize:13,color:'var(--t2)',fontFamily:'var(--sans)'}}>monthly contribution</div>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <button onClick={()=>{const v=Math.max(0,projMonthly-100);setProjMonthly(v);saveProjSetting('mylo_proj_monthly',v);}}
                    style={{width:32,height:32,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'50%',fontFamily:'var(--sans)',fontSize:18,color:'var(--t2)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>−</button>
                  <div style={{fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',minWidth:64,textAlign:'center'}}>{'$'+projMonthly.toLocaleString()}</div>
                  <button onClick={()=>{const v=projMonthly+100;setProjMonthly(v);saveProjSetting('mylo_proj_monthly',v);}}
                    style={{width:32,height:32,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'50%',fontFamily:'var(--sans)',fontSize:18,color:'var(--t2)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>+</button>
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px'}}>
                <div style={{fontSize:13,color:'var(--t2)',fontFamily:'var(--sans)'}}>assumed return</div>
                <div style={{display:'flex',gap:4}}>
                  {[5,6,7,8,9].map(r => (
                    <button key={r} onClick={()=>setProjReturn(r)}
                      style={{fontFamily:'var(--sans)',fontSize:12,padding:'5px 10px',borderRadius:'var(--pill)',
                        border:'0.5px solid '+(projReturn===r?'var(--accent)':'var(--b2)'),
                        background:projReturn===r?'var(--accent-w)':'transparent',
                        color:projReturn===r?'var(--accent)':'var(--t2)',
                        cursor:'pointer',fontWeight:projReturn===r?600:400}}>{r}%</button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{padding:'10px 16px 14px',borderTop:'0.5px solid var(--b1)',fontFamily:'var(--sans)',fontSize:11,color:'var(--t4)',lineHeight:1.5,opacity:0.7}}>
              {'$'+projMonthly.toLocaleString()+'/mo · '+projReturn+'% annual return · compound monthly'}
            </div>
          </div>


          <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',padding:'18px 20px',marginBottom:8}}>
            <div style={{fontFamily:'var(--sans)',fontSize:36,fontWeight:300,color:'var(--ink)',letterSpacing:'-0.6px',lineHeight:1}}>{fmtCurrency(totalPortfolio)}</div>
            <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)',marginTop:4}}>total portfolio</div>
          </div>
          {showAccAdd ? (
            <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',padding:'14px 16px',marginBottom:8}}>
              <div style={{display:'flex',gap:8,marginBottom:8}}>
                <input placeholder="account name" value={accForm.name} autoFocus
                  onChange={e => setAccForm(p => ({...p, name: e.target.value}))}
                  style={{flex:1,height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:15,color:'var(--ink)',outline:'none'}} onFocus={e=>e.target.style.borderColor='var(--accent)'} onBlur={e=>e.target.style.borderColor='var(--b2)'}/>
                <select value={accForm.type} onChange={e => setAccForm(p => ({...p, type: e.target.value}))}
                  style={{width:120,height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 8px',fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',outline:'none',appearance:'none'}}>
                  {ACC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{display:'flex',gap:8}}>
                <input type="number" inputMode="decimal" placeholder="current balance"
                  value={accForm.balance} onChange={e => setAccForm(p => ({...p, balance: e.target.value}))}
                  onKeyDown={e => { if (e.key === 'Enter') addAccount(); }}
                  style={{flex:1,height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:16,color:'var(--ink)',outline:'none'}}/>
                <input placeholder="note" value={accForm.note}
                  onChange={e => setAccForm(p => ({...p, note: e.target.value}))}
                  style={{flex:1,height:44,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:15,color:'var(--ink)',outline:'none'}}/>
              </div>
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <button onClick={addAccount} disabled={!accForm.name || !accForm.balance}
                  style={{flex:1,height:44,background:'var(--accent)',border:'none',borderRadius:'var(--r2)',fontFamily:'var(--sans)',fontSize:13,fontWeight:500,color:'var(--on-accent)',cursor:'pointer',opacity:(!accForm.name||!accForm.balance)?0.4:1}}>add account</button>
                <button onClick={() => setShowAccAdd(false)}
                  style={{height:44,padding:'0 16px',background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',fontFamily:'var(--sans)',fontSize:13,color:'var(--t2)',cursor:'pointer'}}>cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAccAdd(true)}
              style={{width:'100%',height:44,background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',fontFamily:'var(--sans)',fontSize:13,color:'var(--accent)',cursor:'pointer',marginBottom:8}}>
              + add account
            </button>
          )}
          {accounts.length === 0 ? (
            <div style={{textAlign:'center',padding:'40px 0',fontFamily:'var(--sans)',fontSize:13,color:'var(--t4)'}}>no accounts yet — add your 401k, Roth IRA, or brokerage</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {accounts.map(acc => (
                <div key={acc.id} style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:'var(--r3)',overflow:'hidden'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px'}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                        <div style={{fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)'}}>{acc.name}</div>
                        <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t4)',background:'var(--s2)',borderRadius:'var(--r)',padding:'1px 6px',border:'0.5px solid var(--b1)'}}>{acc.type}</div>
                      </div>
                      {acc.note && <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)',marginTop:2}}>{acc.note}</div>}
                      <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t4)',marginTop:2}}>updated {acc.updatedAt || 'never'}</div>
                    </div>
                    <div style={{fontFamily:'var(--sans)',fontSize:20,fontWeight:300,color:'var(--ink)',letterSpacing:'-0.3px',flexShrink:0}}>{fmtCurrency(acc.balance||0)}</div>
                  </div>
                  <div style={{padding:'8px 16px',display:'flex',gap:8,alignItems:'center'}}>
                    <input type="number" inputMode="decimal" placeholder="update balance"
                      onBlur={e => { if (e.target.value && parseFloat(e.target.value) !== acc.balance) { updateAccountBalance(acc.id, e.target.value); e.target.value=''; }}}
                      onKeyDown={e => { if (e.key==='Enter' && e.target.value) { updateAccountBalance(acc.id, e.target.value); e.target.value=''; e.target.blur(); }}}
                      style={{flex:1,height:36,background:'var(--s2)',border:'0.5px solid var(--b2)',borderRadius:'var(--r2)',padding:'0 10px',fontFamily:'var(--sans)',fontSize:14,color:'var(--ink)',outline:'none'}}/>
                    <button onClick={() => deleteAccount(acc.id)}
                      style={{background:'transparent',border:'none',color:'var(--t4)',cursor:'pointer',fontSize:16,padding:'4px 10px',minWidth:36,textAlign:'center'}}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
            </div>
          )}
        </div>
      )}


      {financeView === 'goals' && (
        <>
          {/* Add goal form */}
          {showAddGoal && (
            <div className={css.addCard}>
              <input ref={addGoalRef} className={css.addInput} placeholder="what are you tracking?"
                value={goalForm.title} onChange={e => setGoalForm(p => ({ ...p, title: e.target.value }))} autoComplete="off" />
              {/* Type 3-segment */}
              <div className={css.goalTypeToggle}>
                {GOAL_TYPES.map(t => (
                  <button key={t} type="button"
                    className={[css.goalTypeSeg, goalForm.type === t ? css.goalTypeSegActive : ''].filter(Boolean).join(' ')}
                    onClick={() => setGoalForm(p => ({ ...p, type: t }))}>
                    {t}
                  </button>
                ))}
              </div>
              <div className={css.formGrid2}>
                <input className={css.addInput} type="number" min="0"
                  placeholder={goalForm.type === 'debt-payoff' ? 'starting balance' : goalForm.type === 'contribution' ? 'annual limit' : 'target amount'}
                  value={goalForm.targetAmount} onChange={e => setGoalForm(p => ({ ...p, targetAmount: e.target.value }))} />
                <input className={css.addInput} type="number" min="0"
                  placeholder={goalForm.type === 'debt-payoff' ? 'current balance' : goalForm.type === 'contribution' ? 'ytd contributed' : 'saved so far'}
                  value={goalForm.currentAmount} onChange={e => setGoalForm(p => ({ ...p, currentAmount: e.target.value }))} />
              </div>
              <div className={css.formGrid2}>
                <input className={css.addInput} type="date" value={goalForm.targetDate}
                  onChange={e => setGoalForm(p => ({ ...p, targetDate: e.target.value }))} />
                <SelectWrap>
                  <select className={css.addSelect} value={goalForm.priority} onChange={e => setGoalForm(p => ({ ...p, priority: e.target.value }))}>
                    <option value="high">high priority</option>
                    <option value="normal">normal</option>
                    <option value="low">low</option>
                  </select>
                </SelectWrap>
              </div>
              <input className={css.addInput} placeholder="linked account (optional)"
                value={goalForm.linkedAccount} onChange={e => setGoalForm(p => ({ ...p, linkedAccount: e.target.value }))} />
              <button
                className={[css.submitBtn, goalForm.title.trim() && goalForm.targetAmount && goalForm.currentAmount ? css.submitBtnActive : ''].filter(Boolean).join(' ')}
                onClick={addGoal}
                disabled={!goalForm.title.trim() || !goalForm.targetAmount || !goalForm.currentAmount}>
                add goal
              </button>
              <button className={css.cancelLink} onClick={() => { setShowAddGoal(false); resetGoalForm(); }}>cancel</button>
            </div>
          )}

          {sortedGoals.length === 0 ? (
            <EmptyState message="no goals set" cta={{ label:"+ add your first goal", onClick:() => { setShowAddGoal(true); setTimeout(() => addGoalRef.current?.focus(), 50); } }} />
          ) : visibleGoals.length === 0 && goalFilter !== null ? (
            <div style={{ padding:'var(--sp5) var(--sp4)', textAlign:'center', color:'var(--t3)', fontFamily:'var(--sans)', fontSize:'var(--fs-sm)', lineHeight:1.6 }}>
              No goals are currently {goalFilter === 'on-track' ? 'on track' : 'behind'}.
              <br/>Click the active cell again to clear the filter.
            </div>
          ) : (
            <>
              {visibleGoals.map(goal => (
                <div key={goal.id}>
                  {editGoal === goal.id ? (
                    <div className={css.goalCardEdit}>
                      <GoalEditForm
                        data={editGoalData}
                        setData={setEditGoalData}
                        onSave={updateGoal}
                        onCancel={() => setEditGoal(null)}
                        onDelete={() => deleteGoal(goal.id)}
                      />
                    </div>
                  ) : (
                    <GoalCard goal={goal} onEdit={() => { setEditGoal(goal.id); setEditGoalData({ ...goal, targetAmount: String(goal.targetAmount), currentAmount: String(goal.currentAmount), targetDate: goal.targetDate || '', linkedAccount: goal.linkedAccount || '' }); }} />
                  )}
                </div>
              ))}
              {!showAddGoal && (
                <button className={css.addGoalLink} onClick={() => { setShowAddGoal(true); setTimeout(() => addGoalRef.current?.focus(), 50); }}>
                  + add goal
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
